/** palmetto-typescript-rep-api base (stage included), e.g. http://localhost:8009/dev */
export const API_BASE_URL: string = (
  import.meta.env.VITE_APP_REP_API || 'http://localhost:8009/dev'
).replace(/\/$/, '');
/** Every REP route lives under this prefix. */
export const REP_API = `${API_BASE_URL}/api/rep`;

/** palmetto-typescript-realtime-api websocket; empty disables live updates. */
export const REALTIME_WS_URL: string = import.meta.env.VITE_APP_REALTIME_WS || '';

/** Portal cognito account-verify endpoint. */
export const PORTAL_AUTH_URL: string =
  import.meta.env.VITE_APP_PORTAL_AUTH_URL ||
  'https://portal.palmettoeoc.com/prod/public/api/cognito/account';

/** Where users sign in; the portal opens REP with ?id_token=<cognito id_token>. */
export const PORTAL_URL: string =
  import.meta.env.VITE_APP_PORTAL_URL || 'https://portal.palmettoeoc.com';

/** palmetto-serverless-documents (Excel reports: Generate Report menu items). */
export const DOCUMENTS_API: string = (
  import.meta.env.VITE_APP_DOCUMENTS_API ||
  'https://56ussyznlg.execute-api.us-east-1.amazonaws.com/prod'
).replace(/\/$/, '');

/**
 * palmetto-typescript-dashboard-api contact-support endpoint (full URL). The side menu's Contact
 * Support form posts here; it accepts REP's id_token (same Cognito user pool) and emails support.
 */
export const CONTACT_SUPPORT_API_URL: string =
  import.meta.env.VITE_APP_CONTACT_SUPPORT_API ||
  'https://dashboard.cloudeoc.com/prod/api/contact-support';

/** sessionStorage key of the dev/storybook Cognito session (LoginGate); cleared with the app token. */
export const DEV_SESSION_STORAGE_KEY = 'rep-storybook-id-token';

/** sessionStorage key for the id_token handed over by the portal. */
export const TOKEN_STORAGE_KEY = 'palmetto-rep.idToken';

/** The REP site code inside the account's `attributes` grants. */
export const REP_SITE = 'REP';

/** SCEMD - the state group; everyone else is a county user. */
export const STATE_GROUP_ID = 1;

export const APP_TITLE = 'Palmetto REP';
/** What the legacy app shows in its header bars and side menu (REACT_APP_APP_TITLE). */
export const PROGRAM_TITLE = 'REP';
/** Full program name shown in the side menu (legacy drawer / document title). */
export const PROGRAM_NAME = 'Radiological Emergency Preparedness';
export const APP_VERSION = '1.0.0';
export const APP_SUBTITLE = 'Radiological Emergency Preparedness';
export const USER_GUIDE_URL = '/guides/REPUserGuide.pdf';

/** Realtime channels published by the API (libs/realtime.ts REP_CHANNELS). */
export const REP_CHANNELS = {
  lcert: 'rep-lcert',
  lcertNotifications: 'rep-lcert-notifications',
  equipment: 'rep-equipment',
  equipmentNotifications: 'rep-equipment-notifications',
  calibration: 'rep-calibration',
  organizations: 'rep-organizations',
  redistribution: 'rep-redistribution',
  agreements: 'rep-agreements',
  exercises: 'rep-exercises',
  eopa: 'rep-eopa',
  capabilities: 'rep-capabilities',
  capabilityTargets: 'rep-capability-targets',
  milestones: 'rep-milestones',
  trainingClasses: 'rep-training-classes',
  trainingAttendees: 'rep-training-attendees',
  trainingStatus: 'rep-training-status',
  fileLibrary: 'rep-filelibrary',
} as const;

/** Nuclear plants (rep_plant ids) and their EPZ counties. */
export const PLANTS = [
  { id: 1, name: 'Catawba Plant', short: 'Catawba', region: 'region-2' },
  { id: 2, name: 'Oconee Plant', short: 'Oconee', region: 'region-1' },
  { id: 3, name: 'Robinson Plant', short: 'Robinson', region: 'region-4' },
  { id: 4, name: 'Vogtle Electric Plant', short: 'Vogtle', region: 'region-6' },
  { id: 5, name: 'V.C. Summer Plant', short: 'V.C. Summer', region: 'region-3' },
] as const;

export const RISK_COUNTIES: Record<number, string[]> = {
  1: ['York'],
  2: ['Oconee', 'Pickens'],
  3: ['Chesterfield', 'Darlington', 'Lee'],
  4: ['Allendale', 'Aiken', 'Barnwell'],
  5: ['Fairfield', 'Lexington', 'Newberry', 'Richland'],
};

export const HOST_COUNTIES: Record<number, string[]> = {
  1: ['Cherokee', 'Chester', 'Lancaster', 'Union'],
  2: ['Anderson', 'Greenville'],
  3: ['Lee', 'Chesterfield', 'Florence'],
  4: ['Aiken', 'Allendale'],
  5: [],
};

/** All 46 SC counties (training status map). */
export const SC_COUNTIES = [
  'Abbeville',
  'Aiken',
  'Allendale',
  'Anderson',
  'Bamberg',
  'Barnwell',
  'Beaufort',
  'Berkeley',
  'Calhoun',
  'Charleston',
  'Cherokee',
  'Chester',
  'Chesterfield',
  'Clarendon',
  'Colleton',
  'Darlington',
  'Dillon',
  'Dorchester',
  'Edgefield',
  'Fairfield',
  'Florence',
  'Georgetown',
  'Greenville',
  'Greenwood',
  'Hampton',
  'Horry',
  'Jasper',
  'Kershaw',
  'Lancaster',
  'Laurens',
  'Lee',
  'Lexington',
  'Marion',
  'Marlboro',
  'McCormick',
  'Newberry',
  'Oconee',
  'Orangeburg',
  'Pickens',
  'Richland',
  'Saluda',
  'Spartanburg',
  'Sumter',
  'Union',
  'Williamsburg',
  'York',
];

/** Statuses the Letters of Certification move through, with their pill colors. */
export const LCERT_STATUS = {
  incomplete: 'Incomplete',
  submitted: 'Submitted',
  approved: 'Approved',
  changesRequested: 'Changes requested',
} as const;

export const STATUS_COLORS: Record<string, string> = {
  Incomplete: '#757575',
  Submitted: '#1976D2',
  Approved: '#2E7D32',
  'Changes requested': '#C62828',
  'Not Started': '#757575',
  Accepted: '#2E7D32',
  Declined: '#C62828',
  Overdue: '#C62828',
  Due: '#EF6C00',
  Current: '#2E7D32',
  Completed: '#2E7D32',
  Pending: '#EF6C00',
};
export const STATUS_FALLBACK_COLOR = '#616161';
